/**
 * Predictive Search Web Component
 * Uses Shopify's /search/suggest API for live search results
 * Mixes products and articles in search results
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.results = null;
    this.abortController = null;
    this.debounceTimer = null;
    this.hasLoadedOnce = false;
  }

  connectedCallback() {
    // Look for input in parent dialog first, then within self
    const dialog = this.closest('store-dialog');
    this.input = dialog?.querySelector('[data-search-input]') || this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');

    if (!this.input || !this.results) {
      console.warn('PredictiveSearch: Missing input or results element');
      return;
    }

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    // Listen for dialog open event to lazy load on first open
    this.onDialogOpen = this.handleDialogOpen.bind(this);
    window.addEventListener('store:dialog:open', this.onDialogOpen);
  }

  handleDialogOpen(event) {
    // Only load if this is the search dialog and we haven't loaded yet
    if (event.detail?.id === 'search' && !this.hasLoadedOnce) {
      this.hasLoadedOnce = true;
      this.loadDefaultProducts();

      // Auto-focus input when dialog opens
      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
    }
  }

  async loadDefaultProducts() {
    // Show spinner if results are empty
    if (this.results.innerHTML.trim() === '') {
      this.showLoadingSpinner();
    }

    try {
      // Shopify API max is 10 per resource type
      // Fetch 10 products (max allowed) and 8 articles
      const baseUrl = `${window.Shopify?.routes?.root || '/'}search/suggest`;

      // Make two parallel requests for different limits
      const [productsResponse, articlesResponse] = await Promise.all([
        fetch(`${baseUrl}?q=*&resources[type]=product&resources[limit]=10&section_id=predictive-search-results`),
        fetch(`${baseUrl}?q=*&resources[type]=article&resources[limit]=8&section_id=predictive-search-results`)
      ]);

      if (!productsResponse.ok && !articlesResponse.ok) return;

      // Parse both responses
      const [productsText, articlesText] = await Promise.all([
        productsResponse.ok ? productsResponse.text() : '',
        articlesResponse.ok ? articlesResponse.text() : ''
      ]);

      // Extract results from both
      let allResults = [];

      if (productsText) {
        const html = new DOMParser().parseFromString(productsText, 'text/html');
        const products = html.querySelectorAll('#shopify-section-predictive-search-results [data-resource-type="product"]');
        allResults.push(...Array.from(products));
      }

      if (articlesText) {
        const html = new DOMParser().parseFromString(articlesText, 'text/html');
        const articles = html.querySelectorAll('#shopify-section-predictive-search-results [data-resource-type="article"]');
        allResults.push(...Array.from(articles));
      }

      if (allResults.length > 0) {
        this.results.innerHTML = allResults.map(el => el.outerHTML).join('');
        this.setAttribute('results', 'true');
      }
    } catch (error) {
      console.error('Failed to load default products:', error);
    }
  }

  getResourceParams(searchTerm) {
    // For actual search queries, fetch 10 of each type using limit_scope=each
    return `&resources[type]=product,article&resources[limit]=10&resources[limit_scope]=each`;
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.onDialogOpen) {
      window.removeEventListener('store:dialog:open', this.onDialogOpen);
    }
  }

  onInput(event) {
    const searchTerm = event.target.value.trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (searchTerm.length < 2) {
      this.clearResults();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(searchTerm);
    }, 300);
  }

  onFocus() {
    const searchTerm = this.input.value.trim();
    if (searchTerm.length >= 2 && this.results.innerHTML.trim() === '') {
      this.fetchResults(searchTerm);
    }
  }

  showLoadingSpinner() {
    this.results.innerHTML = `
      <div class="col-span-full flex items-center justify-center min-h-[200px]">
        <svg class="animate-spin h-8 w-8 text-[#064833]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `;
  }

  async fetchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.setAttribute('loading', '');
    this.showLoadingSpinner();

    try {
      // Build resource params based on active tab
      const resourceParams = this.getResourceParams(searchTerm);

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(searchTerm)}${resourceParams}&section_id=predictive-search-results`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) throw new Error('Search request failed');

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = html.querySelector('#shopify-section-predictive-search-results')?.innerHTML || '';

      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', resultsMarkup ? 'true' : 'false');

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
        this.results.innerHTML = '<p class="p-4 text-sm text-gray-500">Search unavailable</p>';
      }
    } finally {
      this.removeAttribute('loading');
    }
  }

  clearResults() {
    this.results.innerHTML = '';
    this.removeAttribute('results');
    // Reload default products when cleared (only if we've loaded before)
    if (this.hasLoadedOnce) {
      this.loadDefaultProducts();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
